import CampaignButtons from '@/components/campaign-buttons'
import { useCampaignClient } from '@/lib/api'
import { invalidateCampaignQueries } from '@/lib/cache-utils'
import { activeStepState } from '@/store/atoms/active-step'
import { isLayout } from '@alphabait/ui/form'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useOutletContext, useParams } from 'react-router-dom'
import { useSetRecoilState } from 'recoil'
import AlphaFormBuilder from '../../components/form/builder/alpha-form-builder'
import { SortableList } from '../../components/form/builder/components/sortable-list'
import { DndQuestionnaire } from '../../components/form/builder/dnd-questionnaire'
import { AlphaTypes } from '../../components/form/custom-types'
import { GetCampaignFragment } from '@/types/campaign'

export default function QuestionnaireComponent({
  referral,
}: {
  referral: boolean
}) {
  const navigate = useNavigate()
  const { id } = useParams()
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const campaignClient = useCampaignClient()
  const { campaign } = useOutletContext<{ campaign: GetCampaignFragment }>()
  const [questionnaire, setQuestionnaire] = useState<AlphaTypes[]>([])
  const setActiveStep = useSetRecoilState(activeStepState)
  const [hasEmptyRequiredFields, setHasEmptyRequiredFields] = useState(false)

  const saveQuestionnaireMutation = useMutation({
    mutationFn: (data: { id: string; questionnaire: any; referral: boolean }) =>
      campaignClient.saveQuestionnaire.mutate({
        params: { id: data.id },
        body: data,
      }),
  })
  const onSubmit = (isDraft: boolean) => {
    // Skip validation for drafts
    if (isDraft) {
      submitForm(isDraft)
      return
    }

    const hasEmptyFields = incompleteFields.length > 0
    setHasEmptyRequiredFields(hasEmptyFields)

    if (!hasEmptyFields) {
      submitForm(isDraft)
    }
  }

  const submitForm = (isDraft: boolean) => {
    if (!id) return

    saveQuestionnaireMutation.mutate(
      {
        id,
        questionnaire: {
          form: questionnaire,
        },
        referral,
      },
      {
        onSuccess: (response) => {
          if (response.status === 200) {
            invalidateCampaignQueries(queryClient, id)

            if (!isDraft && !referral && campaign.referralCampaign) {
              navigate(`/campaigns/referral-questionnaire/${id}`)
            } else if (!isDraft) {
              navigate(`/campaigns/incentives/${id}`)
            }
          }
        },
        onError: (error) => {
          console.error('Failed to save questionnaire:', error)
        },
      },
    )
  }

  // Single source of truth for which questions are incomplete and why,
  // shared by submit validation and the summary list below.
  const incompleteFields = useMemo(
    () =>
      questionnaire.flatMap((field: AlphaTypes) => {
        const label =
          field.uiOptions &&
          typeof field.uiOptions === 'object' &&
          'label' in field.uiOptions
            ? ((field.uiOptions.label as string | undefined) ?? '')
            : ''
        const hasEmptyOptions =
          'options' in field &&
          Array.isArray(field.options) &&
          field.options.length === 0
        const hasEmptyLabel = label.trim().length === 0

        if (!hasEmptyOptions && !hasEmptyLabel) return []
        return [
          {
            id: 'id' in field ? field.id : undefined,
            name: label || t('questionnaire_builder.unnamed_field'),
            reason: hasEmptyLabel
              ? t('questionnaire_builder.missing_label')
              : t('questionnaire_builder.missing_options'),
          },
        ]
      }),
    [questionnaire, t],
  )

  useEffect(() => setActiveStep(referral ? 3 : 2), [setActiveStep, referral])

  useEffect(() => {
    const questionnaire = referral
      ? campaign.referralQuestionnaire
      : campaign.questionnaire

    setQuestionnaire(questionnaire?.form ?? [])
  }, [campaign.questionnaire, campaign.referralQuestionnaire, referral])

  // Memoize Wrapper component to prevent re-creation on every render
  const Wrapper = useCallback(
    ({
      children,
      field,
    }: {
      children: React.ReactNode
      field: AlphaTypes
    }) => {
      if (isLayout(field) || !field.id) return []
      return (
        <SortableList.Item id={field.id} key={field.id}>
          <>{children}</>
        </SortableList.Item>
      )
    },
    [],
  )

  // Memoize dndOptions to prevent re-creation on every render
  const dndOptions = useMemo(
    () => ({
      customWrapper: Wrapper,
      dndQuestionnaire: DndQuestionnaire,
      setFields: setQuestionnaire,
    }),
    [Wrapper, setQuestionnaire],
  )

  return (
    <div className="w-full">
      <div className="flex flex-row items-center pb-4 gap-2">
        <p className="text-2xl font-bold">
          {t('questionnaire_builder.questionnaire')}
        </p>
        {referral && (
          <p className="text-black-400 bg-violet-800 rounded px-1">
            {t('dashboard_labels.referral_campaign')}
          </p>
        )}
      </div>
      <p>{t('questionnaire_builder.questionnaire_description')}</p>

      <AlphaFormBuilder
        fields={questionnaire}
        dndOptions={dndOptions}
        campaignId={id}
      />
      {hasEmptyRequiredFields && incompleteFields.length > 0 && (
        <div className="grid justify-end text-primary mt-4">
          <p className="mt-3 text-sm text-primary">
            {t('questionnaire_builder.please_edit_fields')}
          </p>
          <ul className="list-disc pl-5 mt-2 text-primary">
            {incompleteFields.map((field, index) => (
              <li key={field.id ?? index}>
                <span className="font-medium">{field.name}</span> — {field.reason}
              </li>
            ))}
          </ul>
        </div>
      )}
      <CampaignButtons
        onSubmit={() => onSubmit(false)}
        onSaveDraft={() => onSubmit(true)}
      />
    </div>
  )
}
